// /admin/users — org user list with inline role editor (T084).
//
// Layout: a table of users with email, name, role, and a "Save" button per
// row. The role select is a native <select> pre-populated with the current
// role; pressing Save PATCHes the user and refreshes the row in the SWR
// cache via mutate().
//
// Role changes are buffered per-row so the admin can adjust multiple rows
// before saving (one PATCH per row, no batch endpoint yet).
//
// Only org_admin users can reach this route — the RequireRole middleware on
// the backend will 403 anyone else, and the frontend guards the nav link.

import { useState } from 'react';
import useSWR from 'swr';
import { Check, Loader2, ShieldCheck, UserCog } from 'lucide-react';
import {
  AdminUser,
  ApiError,
  USER_ROLES,
  UserRole,
  adminUsersApi,
  fetcher,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

const USERS_KEY = '/api/v1/admin/users';

export function AdminUsers() {
  const { data, error, isLoading, mutate } = useSWR<AdminUser[]>(
    USERS_KEY,
    fetcher,
  );

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        title="Users"
        description="Manage org members and their roles. Changes take effect immediately."
      />

      <UsersTableBody isLoading={isLoading} error={error} users={data ?? []} onMutate={mutate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface TableBodyProps {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly users: AdminUser[];
  readonly onMutate: (updater: (cur: AdminUser[] | undefined) => AdminUser[]) => void;
}

function UsersTableBody({ isLoading, error, users, onMutate }: TableBodyProps) {
  if (isLoading) {
    return (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading users…</span>
      </div>
    );
  }
  if (error) return <ErrorBanner error={error} />;
  if (users.length === 0) return <EmptyState />;

  return (
    <Table>
      <THead>
        <TR>
          <TH>User</TH>
          <TH>Email</TH>
          <TH>Role</TH>
          <TH srOnly className="w-24">Actions</TH>
        </TR>
      </THead>
      <TBody>
        {users.map((u) => (
          <UserRow key={u.id} user={u} onSaved={(updated) => {
            onMutate((cur) =>
              (cur ?? []).map((x) => (x.id === updated.id ? updated : x)),
            );
          }} />
        ))}
      </TBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Single user row
// ---------------------------------------------------------------------------

interface UserRowProps {
  readonly user: AdminUser;
  readonly onSaved: (updated: AdminUser) => void;
}

function UserRow({ user, onSaved }: UserRowProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>(user.role);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = selectedRole !== user.role;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const updated = await adminUsersApi.updateRole(user.id, selectedRole);
      onSaved(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        msg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        msg = e.message;
      } else {
        msg = 'request failed';
      }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TR>
      <TD primary>
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
            <span className="text-accent text-xs font-semibold uppercase">
              {(user.name || user.email).charAt(0)}
            </span>
          </div>
          <span className="font-medium text-fg truncate max-w-[160px]">
            {user.name || <span className="text-fg-subtle italic">unnamed</span>}
          </span>
        </div>
      </TD>
      <TD className="truncate max-w-[220px]">
        {user.email}
      </TD>
      <TD>
        <div className="flex flex-col gap-1">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as UserRole)}
            className="
              px-2 py-1 rounded-md text-xs
              bg-bg-inset border border-border text-fg
              focus:border-accent/60 focus:bg-bg-elevated
              transition-colors
            "
            aria-label={`Role for ${user.email}`}
          >
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
          {err ? (
            <span role="alert" className="text-[11px] text-danger">{err}</span>
          ) : null}
        </div>
      </TD>
      <TD align="right">
        {saved ? (
          <span className="inline-flex items-center gap-1 text-xs text-success">
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Saved
          </span>
        ) : (
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
              disabled:opacity-30 disabled:cursor-not-allowed
            "
          >
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            ) : (
              <ShieldCheck className="h-3 w-3" aria-hidden="true" />
            )}
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </TD>
    </TR>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roleLabel(role: UserRole): string {
  switch (role) {
    case 'reader': return 'Reader';
    case 'developer': return 'Developer';
    case 'maintainer': return 'Maintainer';
    case 'project_admin': return 'Project Admin';
    case 'org_admin': return 'Org Admin';
  }
}

function EmptyState() {
  return (
    <div className="orb-card p-10 text-center">
      <p className="orb-eyebrow mb-2">empty roster</p>
      <div className="flex justify-center mb-3">
        <UserCog className="h-8 w-8 text-fg-subtle" aria-hidden="true" />
      </div>
      <h2 className="font-display text-xl font-semibold text-fg mb-2">
        No users yet.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        Invite members via the invitation endpoint. Once accepted, they appear
        here for role management.
      </p>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can manage users.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
